import { useEffect, useState } from 'react';
import { useFeatureFlag } from 'common/hooks/useFeatureFlags';
import AlchemyPage from 'common/alchemyComponents/AlchemyPage';
import EditOwnProfile from './EditOwnProfile';

export const LEGACY_EDIT_PROFILE_PATH = '/profile/edit';

/** Marks a server-rendered link that should open the slide-over instead of navigating. */
const LINK_SELECTOR = '[data-edit-own-profile]';

/**
 * Owns the self-service profile slide-over, the global handler React entry points
 * call to open it, and the interception of the server-rendered links. Those links
 * keep pointing at the legacy path, so they still work if this never runs.
 */
export const EditOwnProfileLauncher = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openedFromLegacyPath, setOpenedFromLegacyPath] = useState(false);
  const isEnabled = useFeatureFlag('self_service_profile_edit');

  useEffect(() => {
    window.openEditOwnProfile = () => {
      if (isEnabled) {
        setIsOpen(true);
      } else if (window.selfEditPath) {
        window.location.assign(window.selfEditPath);
      }
    };
  }, [isEnabled]);

  // The links are rendered by Rails, and the content security policy rules out
  // inline handlers on them, so they are intercepted here instead.
  useEffect(() => {
    if (!isEnabled) return undefined;

    const openFromLink = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest(LINK_SELECTOR)) return;

      event.preventDefault();
      setIsOpen(true);
    };

    document.addEventListener('click', openFromLink);

    return () => document.removeEventListener('click', openFromLink);
  }, [isEnabled]);

  // Links, bookmarks and notification emails still point at the legacy page, and
  // the dashboard asks for the slide-over when a first profile is incomplete.
  useEffect(() => {
    if (!isEnabled) return;

    const onLegacyPath = window.location.pathname === LEGACY_EDIT_PROFILE_PATH;
    if (!onLegacyPath && !window.editOwnProfileOnLoad) return;

    setIsOpen(true);
    // Leave the legacy path behind so the URL matches where closing the slide-over
    // takes the user.
    if (onLegacyPath) {
      setOpenedFromLegacyPath(true);
      window.history.replaceState(window.history.state, '', '/');
    }
  }, [isEnabled]);

  // The legacy page renders nothing behind the slide-over, so closing it has to
  // load the dashboard rather than reveal an empty page.
  const close = () => {
    if (openedFromLegacyPath) {
      window.location.assign('/');
      return;
    }

    setIsOpen(false);
  };

  if (!isEnabled || !isOpen) return null;

  // The slide-over is built from Alchemy components, which need their own provider
  // when mounted outside of an Alchemy page.
  return (
    <AlchemyPage noVerticalMargins noHorizontalMargins>
      <EditOwnProfile isOpen onClose={close} />
    </AlchemyPage>
  );
};

export default EditOwnProfileLauncher;
